import { useState, useMemo } from "react";
import {
  ChevronLeft,
  ChevronRight,
  XCircle,
  Monitor,
  MapPin,
  Calendar,
  Clock,
  Stethoscope,
  UserX,
  Plus,
  Trash2,
  Table2,
  LayoutGrid,
  Building2,
  Maximize2,
  Minimize2,
} from "lucide-react";
import { ScheduleCompactView } from "../components/ScheduleCompactView";
import { useFullscreenOverlay } from "@/hooks/useFullscreenOverlay";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { DatePicker } from "@/components/ui/DatePicker";
import { PageLoader } from "@/components/ui/Spinner";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { useAuthStore } from "@/store/auth";
import { formatDate } from "@/utils/formatDate";
import {
  useSchedule,
  useCreateScheduleEntry,
  useDeleteScheduleEntry,
  useSetRemoteHours,
  useCompanyHolidays,
  useCreateCompanyHoliday,
  useDeleteCompanyHoliday,
  useOwedDaysOff,
} from "../hooks/useSchedule";
import type { ScheduleDayInfo, ScheduleUser } from "../types";

// ── Month navigation helpers ───────────────────────────────────────────────

function getMonthStart(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function getMonthEnd(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function fmtDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

// ── Status display config ─────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; border: string }> = {
  present: { label: "W biurze", bg: "bg-green-50", text: "text-green-700", border: "border-green-200" },
  remote: { label: "Zdalnie", bg: "bg-blue-50", text: "text-[var(--color-primary)]", border: "border-blue-200" },
  remote_pending: { label: "Zdalnie?", bg: "bg-blue-50", text: "text-[var(--color-primary)]", border: "border-blue-200" },
  remote_rejected: { label: "Zdalna odrzucona", bg: "bg-red-50", text: "text-red-600", border: "border-red-200" },
  leave: { label: "Urlop", bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  sick_leave: { label: "L4", bg: "bg-purple-100", text: "text-purple-800", border: "border-purple-400" },
  sick_leave_pending: { label: "L4 (oczekuje)", bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-300" },
  uz_present: { label: "W biurze", bg: "bg-green-50", text: "text-green-700", border: "border-green-200" },
  uz_absent: { label: "—", bg: "bg-gray-50", text: "text-gray-400", border: "border-gray-200" },
  uz_absent_explicit: { label: "Nieobecny", bg: "bg-orange-50", text: "text-orange-600", border: "border-orange-200" },
  day_off: { label: "", bg: "bg-gray-100", text: "text-gray-300", border: "border-gray-200" },
  // Days before the employee's employment start — locked, never clickable.
  not_employed: { label: "—", bg: "bg-gray-50", text: "text-gray-300", border: "border-gray-200" },
  // Days after a terminated employee's dismissal date — locked, never clickable.
  employment_ended: { label: "—", bg: "bg-gray-50", text: "text-gray-300", border: "border-gray-200" },
};

function getDayDisplayType(info: ScheduleDayInfo): string {
  if (info.type === "uz_absent" && info.entry_id) return "uz_absent_explicit";
  if (info.type === "remote" && info.status === "pending") return "remote_pending";
  if (info.type === "remote" && info.status === "rejected") return "remote_rejected";
  if (info.type === "sick_leave" && info.status === "pending") return "sick_leave_pending";
  return info.type;
}

// Availability slots
const REMOTE_SLOTS: { value: string; label: string }[] = [
  { value: "06:00", label: "06:00–14:00" },
  { value: "06:30", label: "06:30–14:30" },
  { value: "07:00", label: "07:00–15:00" },
  { value: "07:30", label: "07:30–15:30" },
  { value: "08:00", label: "08:00–16:00" },
  { value: "08:30", label: "08:30–16:30" },
  { value: "09:00", label: "09:00–17:00" },
  { value: "09:30", label: "09:30–17:30" },
  { value: "10:00", label: "10:00–18:00" },
];
const REMOTE_SLOT_LABEL: Record<string, string> = Object.fromEntries(
  REMOTE_SLOTS.map((s) => [s.value, s.label])
);

const POLISH_DAYS_SHORT = ["Pn", "Wt", "Śr", "Cz", "Pt", "So", "Nd"];
const POLISH_MONTHS_FULL = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień",
];

// ── Constants ─────────────────────────────────────────────────────────────

const MIN_MONTH = new Date(2026, 4, 1); // May 2026 — earliest navigable month

function isUzLike(u: ScheduleUser): boolean {
  return u.is_uz || u.employment_fraction_numerator < u.employment_fraction_denominator;
}

/** B2B worker on a working day without an explicit entry — may mark absence. */
function isB2bAbsenceTarget(u: ScheduleUser, info: ScheduleDayInfo): boolean {
  return !!u.is_b2b && !isUzLike(u) && (info.type === "present" || info.type === "remote") && !info.entry_id;
}

// ── Component ─────────────────────────────────────────────────────────────

export function SchedulePage() {
  const user = useAuthStore((s) => s.user);
  const canRequestRemote = user && !user.is_uz && user.role !== "contractor" && user.work_type !== "remote";
  const canReportAbsence = user?.contract_type === "b2b" && user.role !== "contractor";
  const canSeeNote = !!(user?.is_ceo || user?.role === "director" || user?.role === "hr" || user?.role === "accountant");
  const canViewInactive = !!user?.has_view_inactive_in_schedule_permission;
  // Mirrors the backend IsTechnicalDirector gate on POST/DELETE /company-holidays/.
  const canManageCompanyHolidays = !!(user?.is_ceo || (user?.role === "director" && user?.is_technical_director));

  const [monthStart, setMonthStart] = useState(() => getMonthStart(new Date()));
  const monthEnd = useMemo(() => getMonthEnd(monthStart), [monthStart]);
  const [showInactive, setShowInactive] = useState(false);

  const startStr = fmtDate(monthStart);
  const endStr = fmtDate(monthEnd);

  const { data: scheduleData, isLoading } = useSchedule(startStr, endStr, canViewInactive && showInactive);
  const createEntry = useCreateScheduleEntry();
  const deleteEntry = useDeleteScheduleEntry();
  const setRemoteHours = useSetRemoteHours();

  const [viewMode, setViewMode] = useState<"full" | "compact">("full");
  const { isFull, toggle: toggleFull } = useFullscreenOverlay();

  const createCompanyHoliday = useCreateCompanyHoliday();
  const deleteCompanyHoliday = useDeleteCompanyHoliday();
  const { data: companyHolidays } = useCompanyHolidays();
  const [companyHolidayModal, setCompanyHolidayModal] = useState(false);
  const [chDay, setChDay] = useState("");
  const [chDesc, setChDesc] = useState("");
  const [chCompensates, setChCompensates] = useState("");
  const [chError, setChError] = useState("");
  // Who still needs an individual day off (company day off before their first working day).
  const { data: owedDaysOff } = useOwedDaysOff(undefined, companyHolidayModal && canManageCompanyHolidays);
  const chAlreadyGiven = chCompensates
    ? (companyHolidays ?? []).find((ch) => ch.compensates_for === chCompensates)
    : undefined;
  const chCompensatesSameDay = !!chCompensates && chCompensates === chDay;

  const [remoteModal, setRemoteModal] = useState<{ start: string; end: string } | null>(null);
  const [remoteNote, setRemoteNote] = useState("");
  const [remoteError, setRemoteError] = useState("");
  const [hoursModal, setHoursModal] = useState<{ entryId: string; isUz: boolean; currentHour: string | null; ranges: Array<{ start: string; end: string }>; inputError: string } | null>(null);
  const [cancelRemoteConfirm, setCancelRemoteConfirm] = useState<{ entryId: string; error?: string } | null>(null);
  const [uzActionModal, setUzActionModal] = useState<{ dateStr: string; defaultRemote?: boolean; ranges: Array<{ start: string; end: string }>; rangesError: string } | null>(null);
  const [uzWeekendModal, setUzWeekendModal] = useState<{ dateStr: string; entryId: string } | null>(null);
  const [b2bModal, setB2bModal] = useState<{ dateStr: string; canRemote: boolean; error: string } | null>(null);
  const [absenceModal, setAbsenceModal] = useState<{ start: string; end: string; note: string; error: string } | null>(null);

  const monthLabel = `${POLISH_MONTHS_FULL[monthStart.getMonth()]} ${monthStart.getFullYear()}`;

  const handleCellClick = (scheduleUser: ScheduleUser, dateStr: string, info: ScheduleDayInfo) => {
    if (scheduleUser.id !== user?.id) return;
    if (info.type === "day_off" || info.type === "leave" || info.type === "sick_leave" || info.type === "not_employed" || info.type === "employment_ended") return;

    if (isUzLike(scheduleUser)) {
      if (info.type === "uz_absent" && info.entry_id) {
        deleteEntry.mutate(info.entry_id);
      } else if (info.type === "uz_present" && info.entry_id) {
        setUzWeekendModal({ dateStr, entryId: info.entry_id });
      } else if (info.type === "uz_present" && !info.entry_id) {
        setUzActionModal({ dateStr, ranges: [], rangesError: "" });
      } else if (info.type === "uz_absent" && !info.entry_id) {
        createEntry.mutate({ entry_type: "uz_present", start_date: dateStr, end_date: dateStr });
      } else if (info.type === "remote" && !info.entry_id) {
        setUzActionModal({ dateStr, defaultRemote: true, ranges: [{ start: "", end: "" }], rangesError: "" });
      } else if (info.type === "remote" && info.entry_id && info.status === "pending") {
        deleteEntry.mutate(info.entry_id);
      } else if (info.type === "remote" && info.entry_id && (info.status === "approved" || info.status === "confirmed")) {
        setHoursModal({ entryId: info.entry_id, isUz: true, currentHour: info.start_hour, ranges: info.hours_ranges?.length ? info.hours_ranges : [{ start: "", end: "" }], inputError: "" });
      }
    } else {
      if (scheduleUser.is_b2b && info.type === "uz_absent" && info.entry_id) {
        deleteEntry.mutate(info.entry_id);
      } else if (isB2bAbsenceTarget(scheduleUser, info)) {
        setB2bModal({ dateStr, canRemote: info.type === "present" && !!canRequestRemote, error: "" });
      } else if (info.type === "present") {
        setRemoteNote("");
        setRemoteError("");
        setRemoteModal({ start: dateStr, end: dateStr });
      } else if (info.type === "remote" && info.entry_id && info.status === "pending") {
        deleteEntry.mutate(info.entry_id);
      } else if (info.type === "remote" && info.entry_id && info.status === "approved") {
        setHoursModal({ entryId: info.entry_id, isUz: false, currentHour: info.start_hour, ranges: [], inputError: "" });
      }
    }
  };

  const submitAbsence = () => {
    if (!absenceModal) return;
    if (!absenceModal.start || !absenceModal.end) {
      setAbsenceModal({ ...absenceModal, error: "Wybierz datę od i do." });
      return;
    }
    createEntry.mutate(
      { entry_type: "uz_absent", start_date: absenceModal.start, end_date: absenceModal.end, note: absenceModal.note },
      {
        onSuccess: () => setAbsenceModal(null),
        onError: (err: unknown) => {
          const errors = (err as { response?: { data?: { errors?: Record<string, string | string[]> } } })
            ?.response?.data?.errors;
          const first = errors ? Object.values(errors)[0] : undefined;
          const msg = Array.isArray(first) ? first[0] : first;
          setAbsenceModal((m) => (m ? { ...m, error: msg ?? "Nie udało się zgłosić nieobecności." } : m));
        },
      }
    );
  };

  const submitRemote = () => {
    if (!remoteModal) return;
    setRemoteError("");
    createEntry.mutate(
      { entry_type: "remote", start_date: remoteModal.start, end_date: remoteModal.end, note: remoteNote },
      {
        onSuccess: () => setRemoteModal(null),
        onError: (err: unknown) => {
          const detail = (err as { response?: { data?: { errors?: { detail?: string } } } })
            ?.response?.data?.errors?.detail;
          setRemoteError(detail ?? "Błąd podczas składania wniosku.");
        },
      }
    );
  };

  const days = scheduleData?.days ?? [];

  type BranchGroup = { branchId: string | null; branchName: string; users: ScheduleUser[] };
  const branchGroups = useMemo<BranchGroup[]>(() => {
    const sorted = [...(scheduleData?.users ?? [])].sort((a, b) => a.name.localeCompare(b.name, "pl"));
    const map = new Map<string, BranchGroup>();
    const noBranch: ScheduleUser[] = [];
    for (const u of sorted) {
      if (!u.branch) { noBranch.push(u); continue; }
      if (!map.has(u.branch.id)) map.set(u.branch.id, { branchId: u.branch.id, branchName: u.branch.name, users: [] });
      map.get(u.branch.id)!.users.push(u);
    }
    const ownBranchId = user?.branch?.id;
    const groups = [...map.values()].sort((a, b) => {
      if (a.branchId === ownBranchId) return -1;
      if (b.branchId === ownBranchId) return 1;
      return a.branchName.localeCompare(b.branchName, "pl");
    });
    if (noBranch.length > 0) groups.push({ branchId: null, branchName: "Bez oddziału", users: noBranch });
    return groups;
  }, [scheduleData?.users, user?.branch?.id]);

  type RenderItem =
    | { kind: "header"; branchId: string | null; branchName: string }
    | { kind: "user"; schedUser: ScheduleUser; ri: number };
  const renderItems = useMemo<RenderItem[]>(() => {
    const items: RenderItem[] = [];
    const showHeaders = branchGroups.length > 1;
    let ri = 0;
    for (const group of branchGroups) {
      if (showHeaders) items.push({ kind: "header", branchId: group.branchId, branchName: group.branchName });
      for (const schedUser of group.users) items.push({ kind: "user", schedUser, ri: ri++ });
    }
    return items;
  }, [branchGroups]);

  const todayStr = fmtDate(new Date());

  return (
    <div
      className={
        isFull
          ? "fixed inset-0 z-[45] flex flex-col overflow-hidden bg-white dark:bg-gray-900 p-4 sm:p-6"
          : "flex flex-col h-full"
      }
    >
      {/* Header */}
      <div className="mb-4 flex-none">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Harmonogram pracy</h1>
        <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-2" />
        {!isFull && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Obecność, praca zdalna i urlopy — widok miesięczny.
          </p>
        )}
        <p className="text-xs text-[var(--color-primary)] font-medium mt-0.5">
          {user?.is_ceo || user?.role === "hr" || user?.role === "accountant"
            ? "Wszystkie oddziały"
            : user?.branch?.name
            ? `Oddział: ${user.branch.name}`
            : null}
        </p>

        <div className="flex flex-wrap items-center gap-2 mt-3">
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setMonthStart(addMonths(monthStart, -1))}
              disabled={monthStart <= MIN_MONTH}
              className="p-1.5 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[140px] text-center">
              {monthLabel}
            </span>
            <button
              onClick={() => setMonthStart(addMonths(monthStart, 1))}
              className="p-1.5 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <ChevronRight size={16} />
            </button>
            <button
              onClick={() => setMonthStart(getMonthStart(new Date()))}
              className="text-xs px-2.5 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
            >
              Dziś
            </button>
          </div>

          {/* View mode toggle */}
          <div className="flex items-center rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden">
            <button
              onClick={() => setViewMode("full")}
              title="Widok pełny"
              className={`p-1.5 flex items-center gap-1.5 text-xs transition-colors ${
                viewMode === "full"
                  ? "bg-[var(--color-primary)] text-white"
                  : "bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
              }`}
            >
              <Table2 size={14} />
              <span className="hidden sm:inline font-medium">Pełny</span>
            </button>
            <div className="w-px h-full bg-gray-300 dark:bg-gray-600" />
            <button
              onClick={() => setViewMode("compact")}
              title="Widok kompaktowy (Excel)"
              className={`p-1.5 flex items-center gap-1.5 text-xs transition-colors ${
                viewMode === "compact"
                  ? "bg-[var(--color-primary)] text-white"
                  : "bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
              }`}
            >
              <LayoutGrid size={14} />
              <span className="hidden sm:inline font-medium">Kompaktowy</span>
            </button>
          </div>

          {/* Fullscreen toggle */}
          <button
            onClick={toggleFull}
            title={isFull ? "Zamknij pełny ekran (Esc)" : "Pełny ekran"}
            className="p-1.5 flex items-center gap-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {isFull ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            <span className="hidden sm:inline">{isFull ? "Zamknij" : "Pełny ekran"}</span>
          </button>

          {canViewInactive && (
            <label className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer select-none transition-colors">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="accent-red-500"
              />
              Pokaż nieaktywnych
            </label>
          )}

          {canRequestRemote && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => { setRemoteNote(""); setRemoteError(""); setRemoteModal({ start: "", end: "" }); }}
            >
              <Monitor size={14} className="mr-1.5" /> Zgłoś pracę zdalną
            </Button>
          )}
          {canReportAbsence && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setAbsenceModal({ start: "", end: "", note: "", error: "" })}
            >
              <UserX size={14} className="mr-1.5" /> Zgłoś nieobecność
            </Button>
          )}
          {canManageCompanyHolidays && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => { setChDay(""); setChDesc(""); setChCompensates(""); setChError(""); setCompanyHolidayModal(true); }}
            >
              <Building2 size={14} className="mr-1.5" /> Firmowy dzień wolny
            </Button>
          )}
        </div>
      </div>

      {/* Legend */}
      {!isFull && (
        <div className="flex items-center gap-2 mb-4 flex-wrap flex-none">
          {[
            { key: "present", label: "W biurze" },
            { key: "remote", label: "Praca zdalna" },
            { key: "remote_pending", label: "Zdalna (oczekuje)" },
            { key: "remote_rejected", label: "Zdalna odrzucona" },
            { key: "leave", label: "Urlop" },
            { key: "sick_leave", label: "L4" },
            { key: "uz_absent_explicit", label: "Nieobecność (UZ / B2B)" },
          ].map(({ key, label }) => {
            const cfg = STATUS_CONFIG[key];
            return (
              <span
                key={key}
                className={`text-xs px-2 py-0.5 rounded border ${cfg.bg} ${cfg.text} ${cfg.border}`}
              >
                {label}
              </span>
            );
          })}
        </div>
      )}

      {/* Schedule grid */}
      {isLoading ? (
        <div className="flex-1 min-h-0">
          <PageLoader />
        </div>
      ) : (
        <div className="flex-1 min-h-0 bg-white dark:bg-gray-800 overflow-x-auto overflow-y-auto -mx-4 sm:mx-0">
          {viewMode === "compact" ? (
            <ScheduleCompactView
              days={days}
              renderItems={renderItems}
              todayStr={todayStr}
              currentUserId={user?.id}
              canSeeTooltip={canSeeNote}
              onCellClick={handleCellClick}
            />
          ) : (
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 z-30">
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left px-2 sm:px-3 py-2 w-36 sm:w-52 sticky left-0 bg-white dark:bg-gray-800 z-20 border-r border-gray-200 dark:border-gray-700 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Pracownik
                </th>
                {days.map((day) => {
                  const d = new Date(day.date + "T00:00:00");
                  const isToday = day.date === todayStr;
                  return (
                    <th
                      key={day.date}
                      className={`px-0.5 ${isFull ? "py-1" : "py-3"} text-center font-medium min-w-[38px] sm:min-w-[44px] ${
                        isToday ? "border-l-2 border-l-[var(--color-primary)]" : ""
                      } ${
                        !day.is_working_day
                          ? "text-gray-400 dark:text-gray-600 bg-gray-50 dark:bg-gray-800/50"
                          : isToday
                          ? "text-[var(--color-primary)] dark:text-blue-300 bg-white dark:bg-gray-800"
                          : "text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800"
                      }`}
                    >
                      <div className="text-[10px] sm:text-xs">
                        {POLISH_DAYS_SHORT[d.getDay() === 0 ? 6 : d.getDay() - 1]}
                      </div>
                      <div className={`text-sm sm:text-base font-bold ${isToday ? "text-[var(--color-primary)] dark:text-blue-300" : ""}`}>
                        {d.getDate()}
                      </div>
                      {day.is_holiday && (
                        <div
                          className="text-[9px] font-semibold text-red-400 dark:text-red-500 leading-none mt-0.5"
                          title={day.holiday_name ?? undefined}
                        >
                          {day.holiday_name ? "F" : "Ś"}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {renderItems.map((item) => {
                if (item.kind === "header") {
                  return (
                    <tr key={`branch-${item.branchId ?? "none"}`} className="bg-gray-100 dark:bg-gray-700/60">
                      <td className="px-3 py-1.5 sticky left-0 z-10 bg-gray-100 dark:bg-gray-700/60 border-r border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-1.5">
                          <MapPin size={11} className="text-[var(--color-primary)] flex-shrink-0" />
                          <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-600 dark:text-gray-300">
                            {item.branchName}
                          </span>
                        </div>
                      </td>
                      <td colSpan={days.length} className="bg-gray-100 dark:bg-gray-700/60" />
                    </tr>
                  );
                }
                const { schedUser, ri } = item;
                const isCurrentUser = schedUser.id === user?.id;
                const isInactive = schedUser.is_active === false;
                const rowBg = isInactive
                  ? "bg-red-50 dark:bg-red-900/15"
                  : isCurrentUser
                  ? "bg-blue-50 dark:bg-blue-900/20"
                  : ri % 2 === 1
                  ? "bg-gray-50 dark:bg-gray-800/60"
                  : "bg-white dark:bg-gray-800";
                return (
                <tr
                  key={schedUser.id}
                  className={`border-b border-gray-100 dark:border-gray-700/50 ${rowBg}`}
                >
                  <td className={`px-2 sm:px-3 py-2 sticky left-0 z-10 border-r border-gray-200 dark:border-gray-700 ${isInactive ? "bg-red-50 dark:bg-red-900/15" : ri % 2 === 1 ? "bg-gray-50 dark:bg-gray-800/60" : "bg-white dark:bg-gray-800"}`}>
                    <div className="flex items-center gap-1.5" title={isInactive ? "Pracownik nieaktywny (zwolniony)" : undefined}>
                      <UserAvatar
                        fullName={schedUser.name}
                        profilePictureUrl={schedUser.profile_picture_url}
                        size="sm"
                        className="rounded-full"
                        initialsClassName="bg-[var(--color-primary)] text-white font-bold"
                      />
                      <span className={`font-medium text-[11px] leading-tight min-w-0 whitespace-nowrap overflow-hidden text-ellipsis ${isInactive ? "text-red-700 dark:text-red-300" : "text-gray-800 dark:text-gray-200"}`}>
                        {schedUser.name}
                      </span>
                    </div>
                  </td>
                  {days.map((day) => {
                    const info = schedUser.days[day.date];
                    const isToday = day.date === todayStr;
                    if (!info) return <td key={day.date} />;
                    const displayType = getDayDisplayType(info);
                    const cfg = STATUS_CONFIG[displayType] ?? STATUS_CONFIG.present;
                    const isOwnRow = schedUser.id === user?.id;
                    const isClickable =
                      isOwnRow &&
                      day.date >= todayStr &&
                      info.type !== "day_off" &&
                      info.type !== "leave" &&
                      info.type !== "sick_leave" &&
                      ((isUzLike(schedUser) && (
                        info.type === "uz_present" ||
                        info.type === "uz_absent" ||
                        (info.type === "remote" && !info.entry_id) ||
                        (info.type === "remote" && (info.status === "pending" || info.status === "approved" || info.status === "confirmed"))
                      )) ||
                        (!isUzLike(schedUser) &&
                          (info.type === "present" ||
                            isB2bAbsenceTarget(schedUser, info) ||
                            (!!schedUser.is_b2b && info.type === "uz_absent" && !!info.entry_id) ||
                            (info.type === "remote" && info.status === "pending") ||
                            (info.type === "remote" && info.status === "approved"))));

                    return (
                      <td
                        key={day.date}
                        className={`px-0.5 py-1.5 text-center ${isToday ? "border-l-2 border-l-[var(--color-primary)]" : ""} ${!day.is_working_day ? "bg-gray-50 dark:bg-gray-800/50" : ""}`}
                      >
                        {info.type !== "day_off" && (
                          <div
                            onClick={() => isClickable && handleCellClick(schedUser, day.date, info)}
                            title={
                              displayType === "not_employed"
                                ? "Przed rozpoczęciem pracy"
                                : displayType === "employment_ended"
                                ? "Po zakończeniu pracy"
                                : displayType === "remote_rejected" && (isOwnRow || canSeeNote) && info.review_comment
                                ? `Odrzucono: ${info.review_comment}`
                                : isClickable
                                ? isUzLike(schedUser)
                                  ? info.type === "uz_absent" && info.entry_id
                                    ? "Kliknij: cofnij nieobecność"
                                    : info.type === "uz_absent" && !info.entry_id
                                    ? "Kliknij: oznacz pracę w ten dzień"
                                    : info.type === "uz_present" && info.entry_id
                                    ? "Kliknij: cofnij wpis"
                                    : info.type === "uz_present"
                                    ? "Kliknij: oznacz nieobecność lub pracę zdalną"
                                    : info.type === "remote" && !info.entry_id
                                    ? "Kliknij: oznacz nieobecność lub wizytę w biurze"
                                    : (info.type === "remote" && info.status === "approved") || (info.type === "remote" && info.status === "confirmed")
                                    ? "Kliknij: ustaw godziny dostępności"
                                    : "Kliknij: anuluj wniosek"
                                  : schedUser.is_b2b && info.type === "uz_absent"
                                  ? "Kliknij: cofnij nieobecność"
                                  : isB2bAbsenceTarget(schedUser, info)
                                  ? info.type === "present" && canRequestRemote
                                    ? "Kliknij: oznacz nieobecność lub zgłoś pracę zdalną"
                                    : "Kliknij: oznacz nieobecność"
                                  : info.type === "present"
                                  ? "Kliknij: zgłoś pracę zdalną"
                                  : info.type === "remote" && info.status === "approved"
                                  ? "Kliknij: ustaw godziny dostępności"
                                  : "Kliknij: anuluj wniosek"
                                : (isOwnRow || canSeeNote) && info.note
                                ? info.note
                                : cfg.label
                            }
                            className={[
                              "inline-flex items-center justify-center rounded-md px-1 sm:px-1.5 py-0.5 text-[10px] sm:text-xs font-medium border whitespace-nowrap",
                              cfg.bg, cfg.text, cfg.border,
                              isClickable ? "cursor-pointer hover:opacity-80 transition-opacity" : "",
                              displayType === "remote_pending" ? "opacity-70 border-dashed" : "",
                              displayType === "sick_leave_pending" ? "border-dashed" : "",
                            ].filter(Boolean).join(" ")}
                          >
                            {(displayType === "remote" || displayType === "remote_pending") && (
                              <Monitor size={10} className="flex-shrink-0 sm:mr-1" />
                            )}
                            {displayType === "remote_rejected" && (
                              <XCircle size={10} className="flex-shrink-0 sm:mr-1" />
                            )}
                            {displayType === "leave" && (
                              <Calendar size={10} className="flex-shrink-0 sm:mr-1" />
                            )}
                            {(displayType === "sick_leave" || displayType === "sick_leave_pending") && (
                              <Stethoscope size={10} className="flex-shrink-0 sm:mr-1" />
                            )}
                            {(displayType === "present" || displayType === "uz_present") && (
                              <MapPin size={10} className="flex-shrink-0 sm:mr-1" />
                            )}
                            {displayType === "uz_absent_explicit" && (
                              <UserX size={10} className="flex-shrink-0 sm:mr-1" />
                            )}
                            {displayType === "remote" && info.hours_ranges?.length ? (
                              <span className="hidden sm:flex flex-col items-start leading-tight">
                                {info.hours_ranges.map((r, i) => (
                                  <span key={i}>{r.start}–{r.end}</span>
                                ))}
                              </span>
                            ) : (
                              <span className="hidden sm:inline">
                                {displayType === "remote" && info.start_hour
                                  ? REMOTE_SLOT_LABEL[info.start_hour] ?? info.start_hour
                                  : displayType === "leave"
                                  ? (() => { const m = info.note.match(/\(([^)]+)\)/); return m ? `Urlop (${m[1]})` : "Urlop"; })()
                                  : cfg.label || "—"}
                              </span>
                            )}
                            {displayType === "remote" && info.status === "approved" && isOwnRow && (
                              <Clock size={9} className="ml-0.5 opacity-60" />
                            )}
                          </div>
                        )}
                        {/* Individual day off in lieu of a holiday — a working day for everyone else. */}
                        {info.type === "day_off" && day.is_working_day && (
                          <div
                            title={info.note || "Dzień wolny"}
                            className="rounded px-1 py-0.5 text-[10px] font-medium border bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700"
                          >
                            Wolne
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
                );
              })}
            </tbody>
          </table>
          )}
        </div>
      )}

      {/* Availability hours modal */}
      <Modal open={!!hoursModal} onClose={() => setHoursModal(null)} title="Ustaw dostępność zdalną">
        <div className="flex flex-col gap-4">
          {hoursModal?.isUz ? (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Podaj przedziały godzinowe swojej dostępności zdalnej.
              </p>
              <div className="flex flex-col gap-2">
                {hoursModal.ranges.map((range, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="flex-1">
                      {i === 0 && <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Od</label>}
                      <input
                        type="time"
                        value={range.start}
                        onChange={(e) => setHoursModal((m) => {
                          if (!m) return m;
                          const ranges = m.ranges.map((r, j) => j === i ? { ...r, start: e.target.value } : r);
                          return { ...m, ranges, inputError: "" };
                        })}
                        className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                      />
                    </div>
                    <span className={`text-gray-400 ${i === 0 ? "mt-4" : ""}`}>–</span>
                    <div className="flex-1">
                      {i === 0 && <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Do</label>}
                      <input
                        type="time"
                        value={range.end}
                        onChange={(e) => setHoursModal((m) => {
                          if (!m) return m;
                          const ranges = m.ranges.map((r, j) => j === i ? { ...r, end: e.target.value } : r);
                          return { ...m, ranges, inputError: "" };
                        })}
                        className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                      />
                    </div>
                    <button
                      onClick={() => setHoursModal((m) => {
                        if (!m || m.ranges.length <= 1) return m;
                        return { ...m, ranges: m.ranges.filter((_, j) => j !== i), inputError: "" };
                      })}
                      disabled={hoursModal.ranges.length <= 1}
                      className={`${i === 0 ? "mt-4" : ""} p-1.5 rounded text-gray-400 hover:text-red-500 disabled:opacity-20 disabled:cursor-not-allowed transition-colors`}
                      title="Usuń przedział"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={() => setHoursModal((m) => m ? { ...m, ranges: [...m.ranges, { start: "", end: "" }], inputError: "" } : m)}
                className="flex items-center gap-1.5 text-sm text-[var(--color-primary)] hover:opacity-80 w-fit"
              >
                <Plus size={14} /> Dodaj przedział
              </button>
              {hoursModal.inputError && (
                <p className="text-sm text-red-600">{hoursModal.inputError}</p>
              )}
              <div className="flex gap-3">
                <Button
                  loading={setRemoteHours.isPending}
                  disabled={setRemoteHours.isPending}
                  onClick={() => {
                    if (!hoursModal) return;
                    const filled = hoursModal.ranges.filter((r) => r.start || r.end);
                    if (filled.length === 0 || filled.some((r) => !r.start || !r.end)) {
                      setHoursModal((m) => m ? { ...m, inputError: "Uzupełnij wszystkie przedziały lub usuń puste." } : m);
                      return;
                    }
                    setRemoteHours.mutate(
                      { id: hoursModal.entryId, payload: { hours_ranges: filled } },
                      {
                        onSuccess: () => setHoursModal(null),
                        onError: (err: unknown) => {
                          const errs = (err as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data?.errors;
                          const msg = errs?.hours_ranges?.[0] ?? "Błąd zapisu.";
                          setHoursModal((m) => m ? { ...m, inputError: msg } : m);
                        },
                      }
                    );
                  }}
                >
                  Zapisz
                </Button>
                {hoursModal.ranges.some((r) => r.start || r.end) && (
                  <Button
                    variant="secondary"
                    disabled={setRemoteHours.isPending}
                    onClick={() => {
                      if (!hoursModal) return;
                      setRemoteHours.mutate(
                        { id: hoursModal.entryId, payload: { hours_ranges: null } },
                        { onSuccess: () => setHoursModal(null) }
                      );
                    }}
                  >
                    Usuń dostępność
                  </Button>
                )}
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Wybierz godzinę rozpoczęcia 8h bloku dostępności. Pozostali pracownicy zobaczą ten przedział.
              </p>
              <div className="flex flex-wrap gap-2">
                {REMOTE_SLOTS.map((slot) => (
                  <button
                    key={slot.value}
                    onClick={() => {
                      if (!hoursModal) return;
                      setRemoteHours.mutate(
                        { id: hoursModal.entryId, payload: { start_hour: slot.value } },
                        { onSuccess: () => setHoursModal(null) }
                      );
                    }}
                    className={`px-3 py-2 rounded-md text-sm font-medium border transition-colors ${
                      hoursModal?.currentHour === slot.value
                        ? "bg-[var(--color-primary)] text-white border-[var(--color-primary)]"
                        : "bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                    }`}
                  >
                    {slot.label}
                  </button>
                ))}
              </div>
              {hoursModal?.currentHour != null && (
                <button
                  onClick={() => {
                    if (!hoursModal) return;
                    setRemoteHours.mutate(
                      { id: hoursModal.entryId, payload: { start_hour: null } },
                      { onSuccess: () => setHoursModal(null) }
                    );
                  }}
                  className="text-xs text-gray-400 hover:text-red-500 w-fit"
                >
                  Usuń ustawioną dostępność
                </button>
              )}
            </>
          )}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-1">
            <button
              onClick={() => {
                if (!hoursModal) return;
                setCancelRemoteConfirm({ entryId: hoursModal.entryId });
                setHoursModal(null);
              }}
              className="text-sm text-red-500 hover:text-red-700 dark:hover:text-red-400 font-medium w-fit"
            >
              Anuluj pracę zdalną
            </button>
          </div>
        </div>
      </Modal>

      {/* Cancel remote work confirmation modal */}
      <Modal open={!!cancelRemoteConfirm} onClose={() => setCancelRemoteConfirm(null)} title="Anuluj pracę zdalną">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Czy na pewno chcesz anulować pracę zdalną? Dzień zostanie przywrócony do statusu{" "}
            <span className="font-medium text-gray-800 dark:text-gray-200">W biurze</span>.
          </p>
          {cancelRemoteConfirm?.error && (
            <p className="text-sm text-red-600">{cancelRemoteConfirm.error}</p>
          )}
          <div className="flex gap-3">
            <Button
              variant="danger"
              loading={deleteEntry.isPending}
              disabled={deleteEntry.isPending}
              onClick={() => {
                if (!cancelRemoteConfirm) return;
                deleteEntry.mutate(cancelRemoteConfirm.entryId, {
                  onSuccess: () => setCancelRemoteConfirm(null),
                  onError: (err: unknown) => {
                    const detail = (err as { response?: { data?: { errors?: { detail?: string } } } })
                      ?.response?.data?.errors?.detail;
                    setCancelRemoteConfirm((prev) => prev ? { ...prev, error: detail ?? "Błąd podczas anulowania." } : prev);
                  },
                });
              }}
            >
              Anuluj pracę zdalną
            </Button>
            <Button variant="secondary" onClick={() => setCancelRemoteConfirm(null)}>
              Wróć
            </Button>
          </div>
        </div>
      </Modal>

      {/* UZ action modal */}
      <Modal
        open={!!uzActionModal}
        onClose={() => setUzActionModal(null)}
        title={uzActionModal?.defaultRemote ? "Dzień zdalny" : "Oznacz dzień"}
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {uzActionModal?.defaultRemote
              ? <>Ustaw godziny dostępności lub zmień status dnia <span className="font-medium text-gray-800 dark:text-gray-200">{formatDate(uzActionModal?.dateStr, "")}</span>.</>
              : <>Wybierz status dla dnia <span className="font-medium text-gray-800 dark:text-gray-200">{formatDate(uzActionModal?.dateStr, "")}</span>:</>
            }
          </p>

          {uzActionModal?.defaultRemote && (
            <>
              <div className="flex flex-col gap-2">
                {uzActionModal.ranges.map((range, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="flex-1">
                      {i === 0 && <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Od</label>}
                      <input
                        type="time"
                        value={range.start}
                        onChange={(e) => setUzActionModal((m) => {
                          if (!m) return m;
                          const ranges = m.ranges.map((r, j) => j === i ? { ...r, start: e.target.value } : r);
                          return { ...m, ranges, rangesError: "" };
                        })}
                        className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                      />
                    </div>
                    <span className={`text-gray-400 ${i === 0 ? "mt-4" : ""}`}>–</span>
                    <div className="flex-1">
                      {i === 0 && <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Do</label>}
                      <input
                        type="time"
                        value={range.end}
                        onChange={(e) => setUzActionModal((m) => {
                          if (!m) return m;
                          const ranges = m.ranges.map((r, j) => j === i ? { ...r, end: e.target.value } : r);
                          return { ...m, ranges, rangesError: "" };
                        })}
                        className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                      />
                    </div>
                    <button
                      onClick={() => setUzActionModal((m) => {
                        if (!m || m.ranges.length <= 1) return m;
                        return { ...m, ranges: m.ranges.filter((_, j) => j !== i), rangesError: "" };
                      })}
                      disabled={uzActionModal.ranges.length <= 1}
                      className={`${i === 0 ? "mt-4" : ""} p-1.5 rounded text-gray-400 hover:text-red-500 disabled:opacity-20 disabled:cursor-not-allowed transition-colors`}
                      title="Usuń przedział"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={() => setUzActionModal((m) => m ? { ...m, ranges: [...m.ranges, { start: "", end: "" }], rangesError: "" } : m)}
                className="flex items-center gap-1.5 text-sm text-[var(--color-primary)] hover:opacity-80 w-fit"
              >
                <Plus size={14} /> Dodaj przedział
              </button>
              {uzActionModal.rangesError && (
                <p className="text-sm text-red-600">{uzActionModal.rangesError}</p>
              )}
              {uzActionModal.ranges.some((r) => r.start || r.end) && (
                <Button
                  loading={createEntry.isPending || setRemoteHours.isPending}
                  disabled={createEntry.isPending || setRemoteHours.isPending}
                  onClick={() => {
                    if (!uzActionModal) return;
                    const filled = uzActionModal.ranges.filter((r) => r.start || r.end);
                    if (filled.some((r) => !r.start || !r.end)) {
                      setUzActionModal((m) => m ? { ...m, rangesError: "Uzupełnij wszystkie przedziały lub usuń puste." } : m);
                      return;
                    }
                    createEntry.mutate(
                      { entry_type: "remote", start_date: uzActionModal.dateStr, end_date: uzActionModal.dateStr },
                      {
                        onSuccess: (newEntry) => {
                          setRemoteHours.mutate(
                            { id: newEntry.id, payload: { hours_ranges: filled } },
                            {
                              onSuccess: () => setUzActionModal(null),
                              onError: (err: unknown) => {
                                const errs = (err as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data?.errors;
                                setUzActionModal((m) => m ? { ...m, rangesError: errs?.hours_ranges?.[0] ?? "Błąd zapisu godzin." } : m);
                              },
                            }
                          );
                        },
                      }
                    );
                  }}
                >
                  Zapisz godziny
                </Button>
              )}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-3" />
            </>
          )}

          <div className="flex flex-col gap-2">
            {uzActionModal?.defaultRemote && (
              <Button
                variant="secondary"
                disabled={createEntry.isPending || setRemoteHours.isPending}
                onClick={() => {
                  if (!uzActionModal) return;
                  createEntry.mutate(
                    { entry_type: "uz_present", start_date: uzActionModal.dateStr, end_date: uzActionModal.dateStr },
                    { onSuccess: () => setUzActionModal(null) }
                  );
                }}
              >
                <Monitor size={14} className="mr-1.5" /> Obecny w biurze
              </Button>
            )}
            <Button
              variant={uzActionModal?.defaultRemote ? "secondary" : "primary"}
              loading={!uzActionModal?.defaultRemote && createEntry.isPending}
              disabled={createEntry.isPending || setRemoteHours.isPending}
              onClick={() => {
                if (!uzActionModal) return;
                createEntry.mutate(
                  { entry_type: "uz_absent", start_date: uzActionModal.dateStr, end_date: uzActionModal.dateStr },
                  { onSuccess: () => setUzActionModal(null) }
                );
              }}
            >
              <UserX size={14} className="mr-1.5" /> Oznacz nieobecność
            </Button>
            {!uzActionModal?.defaultRemote && (
              <Button
                variant="secondary"
                disabled={createEntry.isPending}
                onClick={() => {
                  if (!uzActionModal) return;
                  createEntry.mutate(
                    { entry_type: "remote", start_date: uzActionModal.dateStr, end_date: uzActionModal.dateStr },
                    { onSuccess: () => setUzActionModal(null) }
                  );
                }}
              >
                <Monitor size={14} className="mr-1.5" /> Praca zdalna (wniosek)
              </Button>
            )}
          </div>
          <button
            onClick={() => setUzActionModal(null)}
            className="text-xs text-gray-400 hover:text-gray-600 w-fit"
          >
            Anuluj
          </button>
        </div>
      </Modal>

      {/* B2B day action modal — absence on working days only */}
      <Modal open={!!b2bModal} onClose={() => setB2bModal(null)} title="Oznacz dzień">
        <div className="flex flex-col gap-4">
          {b2bModal && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Wybierz status dla dnia{" "}
              <span className="font-medium text-gray-800 dark:text-gray-200">{formatDate(b2bModal.dateStr)}</span>:
            </p>
          )}
          <div className="flex flex-col gap-2">
            <Button
              loading={createEntry.isPending}
              disabled={createEntry.isPending}
              onClick={() => {
                if (!b2bModal) return;
                createEntry.mutate(
                  { entry_type: "uz_absent", start_date: b2bModal.dateStr, end_date: b2bModal.dateStr },
                  {
                    onSuccess: () => setB2bModal(null),
                    onError: (err: unknown) => {
                      const detail = (err as { response?: { data?: { errors?: { detail?: string } } } })
                        ?.response?.data?.errors?.detail;
                      setB2bModal((m) => (m ? { ...m, error: detail ?? "Nie udało się oznaczyć nieobecności." } : m));
                    },
                  }
                );
              }}
            >
              <UserX size={14} className="mr-1.5" /> Nieobecność
            </Button>
            {b2bModal?.canRemote && (
              <Button
                variant="secondary"
                disabled={createEntry.isPending}
                onClick={() => {
                  if (!b2bModal) return;
                  setRemoteNote("");
                  setRemoteError("");
                  setRemoteModal({ start: b2bModal.dateStr, end: b2bModal.dateStr });
                  setB2bModal(null);
                }}
              >
                <Monitor size={14} className="mr-1.5" /> Praca zdalna (wniosek)
              </Button>
            )}
          </div>
          {b2bModal?.error && <p className="text-sm text-red-600">{b2bModal.error}</p>}
          <button
            onClick={() => setB2bModal(null)}
            className="text-xs text-gray-400 hover:text-gray-600 w-fit"
          >
            Anuluj
          </button>
        </div>
      </Modal>

      {/* UZ weekend action modal */}
      <Modal open={!!uzWeekendModal} onClose={() => setUzWeekendModal(null)} title="Oznacz dzień">
        <div className="flex flex-col gap-4">
          {uzWeekendModal && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Wybierz status dla dnia{" "}
              <span className="font-medium text-gray-800 dark:text-gray-200">{formatDate(uzWeekendModal.dateStr)}</span>:
            </p>
          )}
          <div className="flex flex-col gap-2">
            <Button
              loading={deleteEntry.isPending}
              disabled={deleteEntry.isPending || createEntry.isPending}
              onClick={() => {
                if (!uzWeekendModal) return;
                deleteEntry.mutate(uzWeekendModal.entryId, { onSuccess: () => setUzWeekendModal(null) });
              }}
            >
              <span className="mr-1.5 font-bold text-base leading-none">—</span> Nie pracuję w ten dzień
            </Button>
            <Button
              variant="secondary"
              disabled={deleteEntry.isPending || createEntry.isPending}
              loading={createEntry.isPending}
              onClick={() => {
                if (!uzWeekendModal) return;
                deleteEntry.mutate(uzWeekendModal.entryId, {
                  onSuccess: () => {
                    createEntry.mutate(
                      { entry_type: "remote", start_date: uzWeekendModal.dateStr, end_date: uzWeekendModal.dateStr },
                      { onSuccess: () => setUzWeekendModal(null) }
                    );
                  },
                });
              }}
            >
              <Monitor size={14} className="mr-1.5" /> Praca zdalna (wniosek)
            </Button>
          </div>
          <button
            onClick={() => setUzWeekendModal(null)}
            className="text-xs text-gray-400 hover:text-gray-600 w-fit"
          >
            Anuluj
          </button>
        </div>
      </Modal>

      {/* Company holiday modal (CEO + technical directors) */}
      <Modal open={companyHolidayModal} onClose={() => setCompanyHolidayModal(false)} title="Firmowy dzień wolny">
        <div className="flex flex-col gap-5">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Wybrany dzień będzie traktowany jak święto — nie wlicza się do urlopu i jest oznaczony w harmonogramie.
          </p>

          {/* Add new */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex flex-col gap-3">
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">Dodaj nowy</p>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Dzień wolny</label>
              <DatePicker
                value={chDay}
                onChange={(v) => { setChDay(v); setChError(""); }}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Opis <span className="text-gray-400 text-xs">(opcjonalny)</span>
              </label>
              <input
                type="text"
                value={chDesc}
                onChange={(e) => setChDesc(e.target.value)}
                placeholder="np. Dzień Firmy"
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Oddawany za dzień <span className="text-gray-400 text-xs">(opcjonalnie — np. święto w sobotę)</span>
              </label>
              <DatePicker
                value={chCompensates}
                onChange={(v) => { setChCompensates(v); setChError(""); }}
              />
              {chAlreadyGiven && (
                <p className="text-sm text-red-600 mt-1">
                  Dzień wolny za {formatDate(chCompensates)} został już oddany (
                  {chAlreadyGiven.start_date === chAlreadyGiven.end_date
                    ? formatDate(chAlreadyGiven.start_date)
                    : `${formatDate(chAlreadyGiven.start_date)} – ${formatDate(chAlreadyGiven.end_date)}`}
                  ).
                </p>
              )}
              {!chAlreadyGiven && chCompensatesSameDay && (
                <p className="text-sm text-red-600 mt-1">
                  Dzień wolny musi przypadać na inny dzień niż ten, za który jest oddawany.
                </p>
              )}
            </div>
            {chError && <p className="text-sm text-red-600">{chError}</p>}
            <Button
              loading={createCompanyHoliday.isPending}
              disabled={createCompanyHoliday.isPending || !!chAlreadyGiven || chCompensatesSameDay}
              onClick={() => {
                if (!chDay) { setChError("Podaj dzień wolny."); return; }
                createCompanyHoliday.mutate(
                  {
                    start_date: chDay,
                    end_date: chDay,
                    description: chDesc.trim() || undefined,
                    compensates_for: chCompensates || null,
                  },
                  {
                    onSuccess: () => { setChDay(""); setChDesc(""); setChCompensates(""); setChError(""); },
                    onError: (err: unknown) => {
                      const errs = (err as { response?: { data?: { errors?: Record<string, string[] | string> } } })?.response?.data?.errors;
                      const first = (v: string[] | string | undefined) => (Array.isArray(v) ? v[0] : v);
                      setChError(
                        first(errs?.compensates_for) ?? first(errs?.end_date) ?? first(errs?.start_date) ?? "Błąd podczas zapisywania.",
                      );
                    },
                  }
                );
              }}
            >
              <Plus size={14} className="mr-1.5" /> Dodaj
            </Button>
          </div>

          {/* Existing company holidays */}
          {companyHolidays && companyHolidays.length > 0 && (
            <div className="flex flex-col gap-2">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">Zdefiniowane</p>
              <div className="flex flex-col gap-1.5 max-h-52 overflow-y-auto">
                {companyHolidays.map((ch) => (
                  <div
                    key={ch.id}
                    className="flex items-center justify-between gap-2 rounded-md border border-gray-200 dark:border-gray-700 px-3 py-2 bg-gray-50 dark:bg-gray-800/50"
                  >
                    <div className="min-w-0">
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                        {ch.start_date === ch.end_date ? formatDate(ch.start_date) : `${formatDate(ch.start_date)} – ${formatDate(ch.end_date)}`}
                      </span>
                      {ch.compensates_for && (
                        <span className="text-xs text-gray-600 dark:text-gray-300 ml-2">za {formatDate(ch.compensates_for)}</span>
                      )}
                      {ch.description && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">{ch.description}</span>
                      )}
                      {(() => {
                        const owed = (owedDaysOff ?? []).filter((o) => o.company_holiday.id === ch.id);
                        if (owed.length === 0) return null;
                        return (
                          <p className="text-xs text-red-600 mt-0.5">
                            Przysługuje jeszcze indywidualnie (zaczęli pracę po tym dniu wolnym):{" "}
                            {owed.map((o) => o.employee_name).join(", ")} — wskaż dzień w panelu Pracownicy.
                          </p>
                        );
                      })()}
                    </div>
                    <button
                      onClick={() => deleteCompanyHoliday.mutate(ch.id)}
                      disabled={deleteCompanyHoliday.isPending}
                      className="p-1.5 rounded text-gray-400 hover:text-red-500 disabled:opacity-40 transition-colors flex-shrink-0"
                      title="Usuń"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Remote work request modal */}
      {/* B2B multi-day absence modal */}
      <Modal open={!!absenceModal} onClose={() => setAbsenceModal(null)} title="Zgłoś nieobecność">
        <div className="flex flex-col gap-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Nieobecność obejmuje tylko dni robocze — weekendy i święta w zakresie zostają dniami wolnymi.
            Wpis nie wymaga zatwierdzenia.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Data od</label>
              <DatePicker
                value={absenceModal?.start ?? ""}
                min={todayStr}
                onChange={(v) => setAbsenceModal((m) => (m ? { ...m, start: v, end: m.end && m.end < v ? v : m.end } : m))}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Data do</label>
              <DatePicker
                value={absenceModal?.end ?? ""}
                min={absenceModal?.start || todayStr}
                onChange={(v) => setAbsenceModal((m) => (m ? { ...m, end: v } : m))}
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Uwagi <span className="text-gray-400 text-xs">(opcjonalne)</span>
            </label>
            <textarea
              rows={2}
              value={absenceModal?.note ?? ""}
              onChange={(e) => setAbsenceModal((m) => (m ? { ...m, note: e.target.value } : m))}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
            />
          </div>
          {absenceModal?.error && <p className="text-sm text-red-600">{absenceModal.error}</p>}
          <div className="flex gap-3">
            <Button loading={createEntry.isPending} onClick={submitAbsence}>
              <UserX size={14} className="mr-1.5" /> Zgłoś
            </Button>
            <Button variant="secondary" onClick={() => setAbsenceModal(null)}>Anuluj</Button>
          </div>
        </div>
      </Modal>

      <Modal open={!!remoteModal} onClose={() => setRemoteModal(null)} title="Wniosek o pracę zdalną">
        <div className="flex flex-col gap-4">
          {remoteModal && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm">
              <p className="text-[var(--color-primary)] dark:text-blue-300 font-medium">
                {remoteModal.start === remoteModal.end
                  ? formatDate(remoteModal.start, "")
                  : `${formatDate(remoteModal.start, "")} – ${formatDate(remoteModal.end, "")}`}
              </p>
              <p className="text-xs text-[var(--color-primary)] dark:text-blue-400 mt-0.5">
                Wniosek wymaga zatwierdzenia przez dyrektora lub kadrową.
              </p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Data od</label>
              <DatePicker
                value={remoteModal?.start ?? ""}
                min="2026-05-01"
                onChange={(v) => setRemoteModal((m) => (m ? { ...m, start: v } : m))}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Data do</label>
              <DatePicker
                value={remoteModal?.end ?? ""}
                min={remoteModal?.start || "2026-05-01"}
                onChange={(v) => setRemoteModal((m) => (m ? { ...m, end: v } : m))}
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Uzasadnienie <span className="text-gray-400 text-xs">(opcjonalne)</span>
            </label>
            <textarea
              rows={2}
              value={remoteNote}
              onChange={(e) => setRemoteNote(e.target.value)}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              placeholder="Powód pracy zdalnej..."
            />
          </div>
          {remoteError && <p className="text-sm text-red-600">{remoteError}</p>}
          <div className="flex gap-3">
            <Button loading={createEntry.isPending} onClick={submitRemote}>
              <Monitor size={14} className="mr-1.5" /> Zgłoś
            </Button>
            <Button variant="secondary" onClick={() => setRemoteModal(null)}>Anuluj</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
